/**
 * DelegateExplainer — explainer for the DELEGATE Structural Duality Principle (Dt).
 *
 * DELEGATE is the level-2 SDP that pairs with PERMIT along the
 * slice ↔ coslice adjunction. It certifies that authority use is not
 * authority grant — a PERMIT witness can be stacked through arbitrary
 * delegation chains without producing a forbidden pattern. With PERMIT
 * (primal) it closes as the ICG-Dt Safety Envelope.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import {
  CalloutBox,
  ExplainerCard,
  GradedAdjunctionDiagram,
  SdpLevelBadge,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import { COMPOSITIONALITY_PRINCIPLE_COLOR, THEOREM_COLOR } from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";

const ACCENT = COMPOSITIONALITY_PRINCIPLE_COLOR.DELEGATE.color;
const LEVEL = COMPOSITIONALITY_PRINCIPLE_COLOR.DELEGATE.level;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-delegate",
  kind: `SSM · Compositionality Principle (S2 · level ${LEVEL})`,
  title: "DELEGATE",
  subtitle: "Dt · slice ↔ coslice · Neutralization Theorem: P",
  accent: ACCENT,
  accentAlt: THEOREM_COLOR.PERMIT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  {
    label: "PERMIT (P, primal Neutralization Theorem)",
    href: "#explainer-permit",
    accent: THEOREM_COLOR.PERMIT,
  },
  { label: "ICG-Dt (Safety Envelope)", href: "#explainer-icg-dt", accent: THEOREM_COLOR.PERMIT },
  { label: "Slice / Coslice", href: "#explainer-slice-category" },
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>DELEGATE</strong> is the safety story for stacking authority across delegation: it
      certifies that <em>authority use is not authority grant</em> — a delegate can exercise a
      capability without thereby being able to mint new ones, by the slice ↔ coslice duality, not by
      inspection.
    </p>
  ),
  intuition: (
    <>
      <p>
        Once you can write "this operation carries its authority" (a PERMIT witness), you
        immediately want to delegate it onward. DELEGATE is the certificate that says delegation is
        safe — receiving a delegated capability (coslice) is dual to exercising one (slice), so a
        delegate cannot mint authority it was never granted.
      </p>
      <p>
        Where PERMIT delivers the guarantee inside one regime, DELEGATE delivers compositionality
        across delegation chains. Together they close as the ICG-Dt Safety Envelope.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        DELEGATE is the level-2 Structural Duality Principle that pairs the slice category{" "}
        <MathInline>{tex`C/\mathrm{Auth}`}</MathInline> (authority use) with the coslice{" "}
        <MathInline>{tex`\mathrm{Auth}/C`}</MathInline> (authority receipt) as a{" "}
        <strong>graded</strong> adjunction.
      </p>
      <p style={{ marginTop: "0.5rem" }}>
        <strong>1-categorical seam vs SDP graded adjunction</strong> (per{" "}
        <code>theory/structural/SEAM-ADJUNCTION-BRIDGE-KAN.md</code>). At the 1-categorical level
        the PERMIT seam declares an <em>absolute empty hom-set</em>: the ungranted operation is
        simply not an object of the slice — it either exists or it does not. As a{" "}
        <strong>level-2</strong> SDP, DELEGATE operates through a 2-categorical <em>graded</em>{" "}
        adjunction indexed by delegation depth: its unit supplies, at each grade, the exact
        capability grant a bridge must produce — structurally inexpressible without the witness,
        expressible with it. Level-2 SDPs come from higher-categorical adjunctions for singleton
        Neutralization Theorems (here, P alone).
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        DELEGATE is what lets the ICG-Dt Safety Envelope reduce the audit surface from "a PERMIT
        witness per delegation hop" to "one envelope" carrying the graded adjunction unit and
        counit.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> If a delegated capability stacks but the joint is
        not a PERMIT witness, DELEGATE rules it out structurally — no graded-adjunction unit
        certifies the chain. Privilege escalation through delegation is{" "}
        <em>structurally inexpressible</em> at the SDP level.
      </p>
      <CalloutBox eyebrow="Safety envelope: ICG-Dt" accent={ACCENT}>
        PERMIT (Neutralization Theorem) <MathInline>{tex`\otimes`}</MathInline> DELEGATE (SDP) =
        ICG-Dt, the single two-sided certificate. Audit surface collapses from{" "}
        <em>(Neutralization Theorem witness) + (SDP witness)</em> to <em>one envelope</em> carrying
        the <MathInline>{tex`\mathrm{slice} \dashv \mathrm{coslice}`}</MathInline> unit and counit.
      </CalloutBox>
    </>
  ),
  references: [
    {
      label: "Theorem Canon — SDPs and ICGs (§7, §7a)",
      path: "docs/reference/Theorem-Canon.md",
    },
    {
      label: "Seam adjunction / bridge Kan theory",
      path: "theory/structural/SEAM-ADJUNCTION-BRIDGE-KAN.md",
      note: "Authoritative treatment of the empty-hom-set seam vs the graded SDP adjunction; AGENTS.md routes here.",
    },
    {
      label: "PERMIT kernel module",
      path: "agda/Kernel/PERMIT/Neutralization.agda",
    },
  ],
};

const DIAGRAM = (
  <>
    <div style={{ marginBottom: "0.5rem" }}>
      <SdpLevelBadge level={LEVEL} accent={ACCENT} />
    </div>
    <GradedAdjunctionDiagram
      accent={ACCENT}
      grade="d"
      ariaLabel="DELEGATE graded adjunction: slice (authority use) ⊣ coslice (authority receipt), indexed by delegation depth d"
    />
  </>
);

export function DelegateExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={DIAGRAM} crossLinks={CROSS_LINKS} />
  );
}
